import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Calendar,
  ChevronLeft,
  FileText,
  Filter,
  Mail,
  Search
} from 'lucide-react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where
} from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { useCurrency } from '../../../utils/currencyUtils';

const PayslipList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();

  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const employeeFilter = new URLSearchParams(location.search).get('employee');

  const fetchPayslips = useCallback(async (loadMore = false, cursor = null) => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    if (!activeBusiness) {
      setError('Select a business before viewing payslips.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      let baseQuery = query(collection(db, 'payslips'), where('businessId', '==', activeBusiness.id));

      if (employeeFilter) {
        baseQuery = query(baseQuery, where('employeeId', '==', employeeFilter));
      }

      if (statusFilter !== 'all') {
        baseQuery = query(baseQuery, where('status', '==', statusFilter));
      }

      baseQuery = query(baseQuery, orderBy('paymentDate', 'desc'), ...(loadMore && cursor ? [startAfter(cursor)] : []), limit(15));

      const snapshot = await getDocs(baseQuery);
      const records = await Promise.all(
        snapshot.docs.map(async (snapshotDoc) => {
          const data = snapshotDoc.data();
          let employee = { id: data.employeeId, firstName: '', lastName: '', email: '', position: 'Worker' };

          if (data.employeeId) {
            const employeeSnapshot = await getDoc(doc(db, 'teamMembers', data.employeeId));
            if (employeeSnapshot.exists()) {
              const employeeData = employeeSnapshot.data();
              employee = {
                id: employeeSnapshot.id,
                firstName: employeeData.firstName || '',
                lastName: employeeData.lastName || '',
                email: employeeData.email || '',
                position: employeeData.position || 'Worker'
              };
            }
          }

          return {
            id: snapshotDoc.id,
            ...data,
            paymentDate: data.paymentDate?.toDate ? data.paymentDate.toDate() : data.paymentDate,
            payPeriod: {
              startDate: data.payPeriod?.startDate?.toDate ? data.payPeriod.startDate.toDate() : data.payPeriod?.startDate,
              endDate: data.payPeriod?.endDate?.toDate ? data.payPeriod.endDate.toDate() : data.payPeriod?.endDate
            },
            employee
          };
        })
      );

      setPayslips((current) => (loadMore ? [...current, ...records] : records));
      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === 15);
    } catch (fetchError) {
      console.error('Error loading payslips:', fetchError);
      setError('Failed to load payslips. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeBusiness, currentUser, employeeFilter, statusFilter]);

  useEffect(() => {
    setLastVisible(null);
    fetchPayslips(false);
  }, [fetchPayslips]);

  const handleMarkSent = async (payslipId) => {
    try {
      await updateDoc(doc(db, 'payslips', payslipId), { status: 'sent' });
      setPayslips((current) =>
        current.map((payslip) => (payslip.id === payslipId ? { ...payslip, status: 'sent' } : payslip))
      );
    } catch (markError) {
      console.error('Error updating payslip:', markError);
      window.alert('Failed to update payslip status.');
    }
  };

  const formatDate = (value) => {
    if (!value) return 'Not set';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Not set' : date.toLocaleDateString();
  };

  const filteredPayslips = payslips.filter((payslip) => {
    if (!searchTerm.trim()) return true;
    const haystack = `${payslip.employee.firstName} ${payslip.employee.lastName} ${payslip.employee.email}`.toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  });

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => navigate(employeeFilter ? '/payroll/payslips' : '/payroll')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center"
        >
          <ChevronLeft size={16} className="mr-1" />
          {employeeFilter ? 'All Payslips' : 'Back to Payroll'}
        </button>
        <h1 className="mt-2 text-xl font-bold text-gray-900">Payslips</h1>
      </div>

      <div className="p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by worker name or email"
              className="w-full rounded-lg border border-gray-200 pl-10 pr-4 py-2.5 text-sm focus:border-[#190192] focus:ring-2 focus:ring-[#190192]/20"
            />
          </div>
          <div className="relative">
            <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-lg border border-gray-200 pl-10 pr-4 py-2.5 text-sm focus:border-[#190192] focus:ring-2 focus:ring-[#190192]/20 appearance-none"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
            </select>
          </div>
        </div>

        {loading && payslips.length === 0 ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 rounded-lg p-6 text-center">
            <AlertCircle size={32} className="mx-auto text-red-500 mb-3" />
            <p className="text-red-800">{error}</p>
          </div>
        ) : filteredPayslips.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600">No payslips found.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Worker</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Period</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Gross</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayslips.map((payslip) => (
                    <tr key={payslip.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {payslip.employee.firstName} {payslip.employee.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{payslip.employee.position || 'Worker'}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <div className="flex items-center">
                          <Calendar size={14} className="mr-2 text-gray-400" />
                          {formatDate(payslip.payPeriod?.startDate)} - {formatDate(payslip.payPeriod?.endDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">
                        {formatDate(payslip.paymentDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatAmount(payslip.grossAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatAmount(payslip.netAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          payslip.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {payslip.status || 'draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-3">
                          <Link to={`/payroll/payslips/${payslip.id}`} className="text-[#190192] hover:text-[#2C0DB5]">
                            View
                          </Link>
                          {payslip.status !== 'sent' && (
                            <button onClick={() => handleMarkSent(payslip.id)} className="text-green-700 hover:text-green-800">
                              <Mail size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => fetchPayslips(true, lastVisible)}
                  disabled={loading}
                  className={`px-4 py-2 border border-gray-300 rounded-lg ${
                    loading ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PayslipList;
